import React, { useState, useContext, useEffect, useRef } from "react";
import { DropDownList } from "@progress/kendo-react-dropdowns";
import { Checkbox, TextBox } from "@progress/kendo-react-inputs";
import { TextArea } from "@progress/kendo-react-inputs";
import { useDispatch, useSelector } from "react-redux";
import { Hint } from "@progress/kendo-react-labels";
import MapContext from "../MapContext";
import { createVectorFromCoordinates } from "../utils/mapUtils";
import {
    calculatePolygonCoordinates,
    createScale,
    createOverlayCoordiantes,
    triggerPrintJob,
    screenShotMap,
} from "./printUtils";
import Style from "ol/style/Style.js";
import { getCenter } from "ol/extent";
import { Stroke, Fill } from "ol/style";
import { setPrintBox } from "../../redux/toolSelectSlice";
import { ImSpinner2 } from "react-icons/im";
import "./Print.css";
import { Button } from "@progress/kendo-react-buttons";

const Print = () => {
    const dispatch = useDispatch();

    const mapTitle = useSelector((state) => state.configData.mapConfig.DisplayName);
    const printTemplates = useSelector((state) => state.configData.printTemplates);
    const defaultTemplate = printTemplates.defaultTemplate;
    const templates = printTemplates.templates;
    let layouts = templates.filter((t) => t.Dimensions !== undefined);
    layouts = layouts.map((l) => ({
        DisplayName: l.DisplayName,
        Id: l.TemplateID,
    }));

    const [title, setTitle] = useState(mapTitle ?? "Map");
    const [titleField, setTitleField] = useState(null);

    const [notesField, setNotesField] = useState(null);
    const [notes, setNotes] = useState("");

    const [scales, setScales] = useState();
    const [scale, setScale] = useState(null);
    const scaleRef = useRef();
    const scalesRef = useRef();

    const [outputFormat, setOutputFormat] = useState(defaultOutputFormat[0]);
    const [resolution, setResolution] = useState(defaultResolutions[0]);

    const [layout, setLayout] = useState(null);
    const currentRes = useRef();

    const dimensionsRef = useRef();
    const idRef = useRef();
    const vectorRef = useRef();

    const lockPreviewRef = useRef(false);
    const [lockPreview, setLockPreview] = useState(false);

    const [printData, setPrintData] = useState();
    const [creatingPrint, setCreatingPrint] = useState(false);

    const { map } = useContext(MapContext);

    const handleUpdateTitle = (e) => {
        setTitle(e.value);
    };
    const handleScaleChange = (e) => {
        setScale(e.value);
        scaleRef.current = e.value.Scale;
    };
    const handleLayoutChange = (e) => {
        idRef.current = e.value.Id;
        setLayout(e.value);
    };
    const handleResolutionChange = (e) => {
        setResolution(e.value);
    };
    const handleFormatChange = (e) => {
        setOutputFormat(e.value);
    };
    const handleNotesChange = (e) => {
        setNotes(e.value);
    };
    const handleCheck = () => {
        lockPreviewRef.current = !lockPreviewRef.current;
        setLockPreview(lockPreviewRef.current);
    };
    const handlePrint = () => {
        setCreatingPrint(true);
        const printInputData = {
            Title: title,
            Format: outputFormat.key,
            Resolution: resolution.Dpi,
            Scale: scale.Scale,
            Notes: notes,
        };

        setPrintData(printInputData);

        let [Width, Height] = dimensionsRef.current.split(",").map(Number);
        const printWidth = Width * 96;
        const printHeight = Height * 96;
        const res = scaleRef.current / 12 / 96;
        // get resolution before printing (for setting map back to original state)

        let center;

        // Get center and hide overlay
        if (lockPreviewRef.current === true) {
            const extent = vectorRef.current
                .getSource()
                .getFeatures()[0]
                .getGeometry()
                .getExtent();
            center = getCenter(extent);
            vectorRef.current.setMap(null);
        }

        // Disable spinner and put overlay back on map
        const printFinishCallback = () => {
            setCreatingPrint(false);
            //   put vector back on map
            if (lockPreviewRef.current === true) {
                vectorRef.current.setMap(map);
            }
        };

        const args = {
            title: printInputData.Title,
            notes: printInputData.Notes,
        };

        const imageArgs = {
            resolution: res,
            center: center,
            dimensions: [printWidth, printHeight],
            elements: ["div.scale-bar"],
        };

        screenShotMap(
            map,
            imageArgs,
            triggerPrintJob,
            args,
            idRef.current,
            printFinishCallback
        );
    };

    // Update values per template
    useEffect(() => {
        if (layout === null) return;
        const template = templates.find((t) => t.DisplayName === layout.DisplayName);

        let textFields = template.TextFields.TextField;
        textFields = Array.isArray(textFields) ? textFields : [textFields];
        textFields.forEach((f) => {
            if (f.DataFieldName === "_Title") {
                setTitleField(f);
                setTitle(f.Value);
            } else if (f.DataFieldName === "_Notes") {
                setNotesField(f);
            } else {
                console.log("handle edge case");
            }
        });

        let scales = template.SupportedMapScales.ScaleItem;
        scales = Array.isArray(scales) ? scales : [scales];

        const res = map.getView().getResolution();
        const currentScale = createScale(map, res);

        scaleRef.current = currentScale.Scale;
        scalesRef.current = [currentScale, ...scales];

        setScales([currentScale, ...scales]);
        setScale(currentScale);

        dimensionsRef.current = template.Dimensions;
    }, [layout]);

    // Runs on component mount
    //   add overlay, add listener to update overlay
    useEffect(() => {
        setLayout({
            DisplayName: defaultTemplate.DisplayName,
            Id: defaultTemplate.TemplateID,
        });
        idRef.current = defaultTemplate.TemplateID;
        let textFields = defaultTemplate.TextFields.TextField;
        textFields = Array.isArray(textFields) ? textFields : [textFields];
        textFields.forEach((f) => {
            if (f.DataFieldName === "_Title") {
                setTitleField(f);
                setTitle(f.Value);
            } else if (f.DataFieldName === "_Notes") {
                setNotesField(f);
            } else {
                console.log("handle edge case");
            }
        });

        let scales = defaultTemplate.SupportedMapScales.ScaleItem;
        scales = Array.isArray(scales) ? scales : [scales];
        const res = map.getView().getResolution();
        const currentScale = createScale(map, res);
        scaleRef.current = currentScale.Scale;
        scalesRef.current = [currentScale, ...scales];

        setScales([currentScale, ...scales]);
        setScale(currentScale);
        dimensionsRef.current = defaultTemplate.Dimensions;
        const overLayCoordinates = createOverlayCoordiantes(
            map,
            scaleRef,
            dimensionsRef.current
        );
        dispatch(setPrintBox(overLayCoordinates));
        const updatePrintBoxOverlay = () => {
            const res = map.getView().getResolution();
            const newScale = createScale(map, res);
            const previousScales = scalesRef.current.filter(
                (s) => s.DisplayName.includes("Current") !== true
            );
            setScales([newScale, ...previousScales]);
            // if resolution changes, update overlay
            if (
                lockPreviewRef.current === false &&
                res.toFixed(6) !== currentRes.current
            ) {
                const overLayCoordinates = createOverlayCoordiantes(
                    map,
                    scaleRef,
                    dimensionsRef.current
                );
                dispatch(setPrintBox(overLayCoordinates));
            }
            currentRes.current = res.toFixed(6);
        };
        map.on("moveend", updatePrintBoxOverlay);
        return () => {
            map.un("moveend", updatePrintBoxOverlay);
            dispatch(setPrintBox(undefined));
        };
    }, []);

    // handle changes to scale, layout and lockpreview
    useEffect(() => {
        //update map polygon
        if (lockPreviewRef.current === true) {
            dispatch(setPrintBox(undefined));

            // create print box coordinates
            const coords = calculatePolygonCoordinates(
                map,
                scaleRef,
                dimensionsRef.current
            );

            // create vector
            vectorRef.current = createVectorFromCoordinates(coords, printStyle);
            vectorRef.current.setMap(map);
        }
        // update map overlay
        else if (lockPreviewRef.current === false) {
            const overLayCoordinates = createOverlayCoordiantes(
                map,
                scaleRef,
                dimensionsRef.current
            );
            dispatch(setPrintBox(overLayCoordinates));
        }
        return () => {
            if (vectorRef.current !== undefined) {
                dispatch(setPrintBox(undefined));
                vectorRef.current.setMap(null);
            }
        };
    }, [lockPreview, scale, layout]);

    return (
        <>
            {layout && (
                <div id="print">
                    <h3 className="title">Print</h3>
                    {titleField && (
                        <div className="print-option">
                            <div>{titleField.DisplayName}</div>
                            <TextBox
                                value={title}
                                onChange={titleField.Editable && handleUpdateTitle}
                            />
                        </div>
                    )}

                    <div className="print-option">
                        <div>Selected Layout</div>{" "}
                        <DropDownList
                            data={layouts}
                            textField="DisplayName"
                            dataItemKey="Id"
                            value={layout}
                            onChange={handleLayoutChange}
                        />
                    </div>
                    <div className="print-option">
                        <div>Output Format</div>{" "}
                        <DropDownList
                            data={defaultOutputFormat}
                            textField="DisplayName"
                            dataItemKey="key"
                            value={outputFormat}
                            onChange={handleFormatChange}
                        />
                    </div>
                    <div className="print-option">
                        <div>Resolution</div>{" "}
                        <DropDownList
                            data={defaultResolutions}
                            textField="DisplayName"
                            dataItemKey="Dpi"
                            value={resolution}
                            onChange={handleResolutionChange}
                        />
                    </div>
                    <div className="print-option">
                        <div>Map Scale</div>
                        <DropDownList
                            data={scales}
                            textField="DisplayName"
                            dataItemKey="Scale"
                            value={scale}
                            onChange={handleScaleChange}
                        />
                    </div>
                    {notesField && (
                        <div className="print-option">
                            <div>{notesField.DisplayName}</div>
                            <TextArea
                                value={notes}
                                onChange={handleNotesChange}
                                maxLength={400}
                                rows={notesField.multiLine === true ? 7 : 1}
                            />
                            <Hint direction={"end"}>{notes.length} / 400</Hint>
                        </div>
                    )}
                    <div className="print-option">
                        <Checkbox onChange={handleCheck} /> Lock print preview with map
                    </div>
                    <div className="print-option">
                        <Button
                            style={{ float: "right" }}
                            onClick={handlePrint}
                            disabled={creatingPrint}>
                            Print
                        </Button>
                    </div>
                    {creatingPrint === true && <ImSpinner2 className="print-spinner" />}
                </div>
            )}
        </>
    );
};

export default Print;

const printStyle = new Style({
    stroke: new Stroke({
        color: "rgba(255, 0, 0, .6)",
        lineDash: [15],
        width: 2,
    }),
    fill: new Fill({
        color: "rgba(255, 0, 0, .1)",
    }),
});

const addExtentToScales = (map, res) => {
    const currentExtentScale = createScale(map, res);
    if (defaultMapScales[0].DisplayName.includes("Current")) {
        defaultMapScales.shift();
    }
    defaultMapScales.unshift(currentExtentScale);
};

const defaultMapScales = [
    { DisplayName: "1 inch = 25 feet", Scale: "300" },
    { DisplayName: "1 inch = 50 feet", Scale: "600" },
    { DisplayName: "1 inch = 100 feet", Scale: "1200" },
    { DisplayName: "1 inch = 200 feet", Scale: "2400" },
    { DisplayName: "1 inch = 400 feet", Scale: "4800" },
];

const defaultOutputFormat = [{ DisplayName: "Pdf", key: ".pdf" }];

const defaultResolutions = [{ DisplayName: "96", Dpi: "96" }];
